// ============================================================
// TESSERACT — 4D Chess
// Core Type Definitions
// ============================================================

// --------------- Dimensions ---------------

/** Plane names for the W-axis */
export const PLANE_NAMES = ['EARTH', 'AETHER', 'VOID', 'FLUX'] as const;
export type PlaneName = typeof PLANE_NAMES[number];

/** Plane code letters for notation */
export const PLANE_CODES: Record<PlaneName, string> = {
  EARTH: 'E',
  AETHER: 'A',
  VOID: 'V',
  FLUX: 'F',
};

export const NUM_PLANES = 4;
export const BOARD_SIZE = 8;
export const FILES = 'abcdefgh';

// --------------- Position ---------------

/** 4D position (spatial only) */
export interface Position {
  readonly x: number;  // 0-7 file (a-h)
  readonly y: number;  // 0-7 rank (1-8)
  readonly w: number;  // 0-3 plane (Earth-Flux)
}

/** 5D position (with temporal axis) */
export interface Position5D extends Position {
  readonly t: number;  // turn number
}

/** Check if two positions are equal (spatial) */
export function posEq(a: Position, b: Position): boolean {
  return a.x === b.x && a.y === b.y && a.w === b.w;
}

/** Create a position */
export function pos(x: number, y: number, w: number): Position {
  return { x, y, w };
}

// --------------- Pieces ---------------

export const PIECE_KINDS = ['pawn', 'knight', 'bishop', 'rook', 'queen', 'king'] as const;
export type PieceKind = typeof PIECE_KINDS[number];

export type Side = 'white' | 'black';

export interface Piece {
  readonly kind: PieceKind;
  readonly side: Side;
  readonly id: string;               // unique ID (survives moves)
  readonly moveCount: number;        // total moves made
  readonly lastMovedTurn: number;    // turn of last move (-1 if never)
  readonly crossDimCooldown: number; // turns until cross-dim allowed (King only)
}

/** Standard material values */
export const PIECE_VALUES: Record<PieceKind, number> = {
  pawn: 100,
  knight: 320,
  bishop: 330,
  rook: 500,
  queen: 900,
  king: 0, // infinite — never captured
};

/** Unicode symbols */
export const PIECE_SYMBOLS: Record<Side, Record<PieceKind, string>> = {
  white: { king: '♔', queen: '♕', rook: '♖', bishop: '♗', knight: '♘', pawn: '♙' },
  black: { king: '♚', queen: '♛', rook: '♜', bishop: '♝', knight: '♞', pawn: '♟' },
};

// --------------- Board State ---------------

/** A cell is either empty or holds a piece */
export type Cell = Piece | null;

/**
 * The 4D grid: grid[w][y][x]
 * - w: plane index (0-3)
 * - y: rank index (0-7, 0=rank 1)
 * - x: file index (0-7, 0=file a)
 */
export type Grid = Cell[][][];

export interface CastlingRights {
  readonly white: { kingSide: boolean; queenSide: boolean };
  readonly black: { kingSide: boolean; queenSide: boolean };
}

export interface BoardState {
  readonly grid: Grid;
  readonly turn: number;
  readonly activePlayer: Side;
  readonly castlingRights: CastlingRights;
  readonly enPassantTarget: Position | null;
  readonly halfMoveClock: number;
  readonly hash: string;
}

// --------------- Moves ---------------

export type CastleSide = 'kingside' | 'queenside';

export interface Move {
  readonly piece: Piece;
  readonly from: Position;
  readonly to: Position;
  readonly capture: Piece | null;
  readonly promotion: PieceKind | null;
  readonly isCrossDimensional: boolean;
  readonly isCheck: boolean;
  readonly isCheckmate: boolean;
  readonly castle: CastleSide | null;
  readonly isEnPassant: boolean;
  readonly notation: string;
  readonly turn: number;
}

// --------------- Game ---------------

export type GameResult =
  | { kind: 'checkmate'; winner: Side }
  | { kind: 'stalemate' }
  | { kind: 'draw'; reason: 'fifty-move' | 'repetition' | 'insufficient' | 'agreement' }
  | { kind: 'resignation'; winner: Side };

export interface Game {
  readonly states: BoardState[];  // indexed by turn (T-axis!)
  readonly moves: Move[];
  readonly result: GameResult | null;
  readonly config: GameConfig;
}

export interface GameConfig {
  readonly planes: number;                // 4 (extendable)
  readonly boardSize: number;             // 8
  readonly crossDimEnabled: boolean;      // true
  readonly timeDimEnabled: boolean;       // false (v2)
  readonly aiEnabled: boolean;
  readonly aiSide: Side;
  readonly aiDepth: number;
}

export const DEFAULT_CONFIG: GameConfig = {
  planes: 4,
  boardSize: 8,
  crossDimEnabled: true,
  timeDimEnabled: false,
  aiEnabled: true,
  aiSide: 'black',
  aiDepth: 3,
};

// --------------- UI State ---------------

export type LayoutMode = 'grid-2x2' | 'hybrid' | 'stacked-3d';

export interface UIState {
  readonly selectedPiece: Position | null;
  readonly validMoves: Position[];
  readonly hoveredSquare: Position | null;
  readonly activePlane: number;         // for hybrid layout
  readonly layoutMode: LayoutMode;
  readonly animating: boolean;
}

// --------------- Events ---------------

export type GameEvent =
  | { kind: 'move'; move: Move }
  | { kind: 'select'; position: Position }
  | { kind: 'deselect' }
  | { kind: 'check'; side: Side; from: Position }
  | { kind: 'checkmate'; winner: Side }
  | { kind: 'stalemate' }
  | { kind: 'promotion'; position: Position; piece: Piece }
  | { kind: 'cross-dim'; from: Position; to: Position };

export type EventHandler = (event: GameEvent) => void;

// --------------- Plane Theming ---------------

export interface PlaneTheme {
  readonly name: PlaneName;
  readonly lightCell: string;
  readonly darkCell: string;
  readonly accent: string;
  readonly accentGlow: string;
  readonly label: string;
}

export const PLANE_THEMES: PlaneTheme[] = [
  { name: 'EARTH',  lightCell: '#2a2a3a', darkCell: '#1a1a28', accent: '#6f9ff0', accentGlow: 'rgba(111,159,240,0.15)', label: 'W=0 · EARTH' },
  { name: 'AETHER', lightCell: '#2a2838', darkCell: '#1a1826', accent: '#c06ff0', accentGlow: 'rgba(192,111,240,0.15)', label: 'W=1 · AETHER' },
  { name: 'VOID',   lightCell: '#2a2832', darkCell: '#1a1822', accent: '#f06f8c', accentGlow: 'rgba(240,111,140,0.15)', label: 'W=2 · VOID' },
  { name: 'FLUX',   lightCell: '#2a2a30', darkCell: '#1a1a20', accent: '#f0c36f', accentGlow: 'rgba(240,195,111,0.15)', label: 'W=3 · FLUX' },
];
